import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/system";
import "../../pages/admin/Sponsors/Sponsors.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "white",
  boxShadow: 24,
  padding: "20px",
  borderRadius: "10px",
};

const IdleTimer = () => {
  // Durations (milliseconds)
  const idleDuration = 15 * 60 * 1000; // 15 minutes
  const modalDuration = 2 * 60 * 1000; // 2 minutes

  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasToken, setHasToken] = useState(!!localStorage.getItem("token"));
  const [justStayed, setJustStayed] = useState(false);

  // Using refs avoids unnecessary re-renders when handling timeouts and channels.
  const modalTimeoutRef = useRef(null);
  const broadcastChannelRef = useRef(
    new BroadcastChannel("idle-timer-channel")
  );

  // Helper to update last active time
  const updateLastActive = useCallback(() => {
    localStorage.setItem("lastActiveTime", Date.now().toString());
  }, []);

  // On mount, initialize last active time and clear modal flag.
  useEffect(() => {
    updateLastActive();
    localStorage.removeItem("isModalVisible");
    setIsModalVisible(false);
  }, [updateLastActive]);

  // Reset idle state by clearing modal and its timeout.
  const resetIdleState = useCallback(() => {
    localStorage.removeItem("isModalVisible");
    setIsModalVisible(false);
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }
  }, []);

  // Helper to broadcast messages to other tabs.
  const broadcastMessage = useCallback((msg) => {
    try {
      broadcastChannelRef.current.postMessage(msg);
    } catch (error) {
      console.error("BroadcastChannel error:", error);
    }
  }, []);

  // On any activity, update lastActiveTime and broadcast an "ACTIVITY" message.
  const handleActivity = useCallback(() => {
    if (!isModalVisible && !justStayed) {
      updateLastActive();
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current);
        modalTimeoutRef.current = null;
      }
      broadcastMessage({ type: "ACTIVITY" });
    }
  }, [isModalVisible, justStayed, updateLastActive, broadcastMessage]);

  // Log out the user, remove token, and broadcast logout.
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.setItem("lastLogoutTime", Date.now().toString());
    resetIdleState();
    broadcastMessage({ type: "LOGOUT" });
    navigate("/login");
  }, [navigate, resetIdleState, broadcastMessage]);

  // Trigger idle state: show modal and start the auto-logout countdown.
  const triggerIdle = useCallback(() => {
    if (localStorage.getItem("token")) {
      localStorage.setItem("isModalVisible", "true");
      setIsModalVisible(true);
      modalTimeoutRef.current = setTimeout(() => {
        logout();
      }, modalDuration);
      broadcastMessage({ type: "IDLE" });
    }
  }, [logout, modalDuration, broadcastMessage]);

  // Handle user clicking "Yes" (stay logged in)
  const handleStayLoggedIn = useCallback(() => {
    resetIdleState();
    setJustStayed(true);
    updateLastActive();
    broadcastMessage({ type: "STAY" });
    setTimeout(() => {
      setJustStayed(false);
    }, 1000);
  }, [resetIdleState, updateLastActive, broadcastMessage]);

  // Handle user clicking "No" (logout)
  const handleUserLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Listen for user activity events.
  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) => window.addEventListener(event, handleActivity));
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [handleActivity]);

  // Check idle status every second.
  useEffect(() => {
    const checkIdle = () => {
      const token = localStorage.getItem("token");
      setHasToken(!!token);
      if (!token) return;
      const lastActive =
        parseInt(localStorage.getItem("lastActiveTime"), 10) || Date.now();
      if (Date.now() - lastActive >= idleDuration && !isModalVisible) {
        triggerIdle();
      }
    };
    checkIdle();
    const interval = setInterval(checkIdle, 1000);
    return () => clearInterval(interval);
  }, [idleDuration, isModalVisible, triggerIdle]);

  // Listen for localStorage changes for multi-tab sync.
  useEffect(() => {
    const onStorageChange = (event) => {
      if (event.key === "token" && !event.newValue) {
        logout();
      }
      if (event.key === "isModalVisible") {
        setIsModalVisible(event.newValue === "true");
      }
      if (event.key === "lastActiveTime") {
        handleActivity();
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, [logout, handleActivity]);

  // Listen for BroadcastChannel messages.
  useEffect(() => {
    const channel = broadcastChannelRef.current;
    const onMessage = (event) => {
      const { type } = event.data;
      if (type === "ACTIVITY") {
        updateLastActive();
        if (isModalVisible) resetIdleState();
      }
      if (type === "IDLE") {
        setIsModalVisible(true);
      }
      if (type === "LOGOUT") {
        logout();
      }
      if (type === "STAY") {
        resetIdleState();
      }
    };
    channel.onmessage = onMessage;
    return () => {
      try {
        channel.close();
      } catch (error) {
        console.error("Error closing channel:", error);
      }
    };
  }, [isModalVisible, logout, resetIdleState, updateLastActive]);

  // Optional: Listen for tab close event.
  useEffect(() => {
    const onBeforeUnload = () => {
      localStorage.setItem("lastClosedTime", Date.now().toString());
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  return (
    <>
      {hasToken && (
        <Modal
          open={isModalVisible}
          onClose={handleStayLoggedIn} // clicking outside triggers stay logged in
          aria-labelledby="idle-modal-title"
          aria-describedby="idle-modal-description"
        >
          <Box sx={modalStyle}>
            <div className="flex flex-col items-center justify-center">
              <p>Your session is about to expire due to inactivity.</p>
              <p>Do you want to stay logged in?</p>
              <div className="flex gap-3 mt-4">
                <button
                  className="navigateConfirmBtn"
                  onClick={handleStayLoggedIn}
                >
                  Yes
                </button>
                <button className="navigateNoBtn" onClick={handleUserLogout}>
                  No
                </button>
              </div>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default IdleTimer;
